// Inline attachment shown inside user / assistant messages. Images and PDF
// first-page previews render eagerly, chat-style (WhatsApp-like sizing);
// other files show a tappable chip that downloads on click.

import { useEffect, useState } from 'react';
import { useChatStore } from '../../store/chatStore';
import type { Attachment } from '../../protocol/types';
import { FileIcon } from '../ui/Icons';
import { Spinner } from '../ui/Spinner';

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

interface Props {
  attachment: Attachment;
  variant: 'user' | 'assistant';
}

export function AttachmentChip({ attachment, variant }: Props) {
  const fetchBlobURL = useChatStore((s) => s.fetchAttachmentBlobURL);

  const isImage = attachment.mimeType.startsWith('image/');
  const isPdf = attachment.mimeType === 'application/pdf';
  const downloadable = Boolean(attachment.url);

  // Source rendered inline: the image itself, or a PDF's first-page preview.
  const mediaSrc = isImage ? attachment.url : isPdf ? attachment.previewUrl ?? null : null;

  const [mediaURL, setMediaURL] = useState<string | null>(null);
  const [mediaError, setMediaError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  // Eagerly fetch inline media on mount so the chat reads as a chat, not a
  // list of click-to-open chips. The blob URL is revoked on unmount.
  useEffect(() => {
    if (!mediaSrc) return;
    let cancelled = false;
    let created: string | null = null;
    setMediaError(false);
    fetchBlobURL(mediaSrc)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        created = u;
        setMediaURL(u);
      })
      .catch(() => {
        if (!cancelled) setMediaError(true);
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [mediaSrc, fetchBlobURL]);

  // Download the full file (or open the full PDF) on tap.
  async function downloadFull() {
    if (!attachment.url) return;
    setBusy(true);
    setError(false);
    try {
      const url = await fetchBlobURL(attachment.url);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  // --- Inline image (sent + received), aspect-preserving, screen-safe ---
  if (isImage && attachment.url && !mediaError) {
    return (
      <div className="inline-block overflow-hidden rounded-xl border border-divider/60 bg-elevated/40">
        {mediaURL ? (
          <img
            src={mediaURL}
            alt={attachment.name}
            onClick={() => window.open(mediaURL, '_blank', 'noopener,noreferrer')}
            className="block h-auto w-auto max-h-[24rem] max-w-[min(20rem,75vw)] cursor-zoom-in object-contain"
          />
        ) : (
          <div className="flex h-40 w-40 items-center justify-center">
            <Spinner size={18} />
          </div>
        )}
      </div>
    );
  }

  // --- PDF first-page preview ("peek") with page count ---
  if (isPdf && attachment.previewUrl && !mediaError) {
    const pages = attachment.pageCount;
    return (
      <button
        type="button"
        onClick={downloadFull}
        disabled={!downloadable || busy}
        title={`${attachment.name} • ${humanSize(attachment.size)}`}
        className="block w-[min(16rem,70vw)] overflow-hidden rounded-xl border border-divider/60 bg-elevated text-left transition-opacity disabled:opacity-60"
      >
        <div className="relative h-44 w-full overflow-hidden bg-white">
          {mediaURL ? (
            <img src={mediaURL} alt="" className="w-full object-cover object-top" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Spinner size={18} />
            </div>
          )}
          {/* Fade the cut-off edge so the page reads as a peek, not a crop. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-elevated to-transparent" />
        </div>
        <div className="flex items-center gap-2 px-2.5 py-1.5 text-xs text-text-primary">
          {busy ? <Spinner size={13} /> : <FileIcon size={13} />}
          <span className="truncate">{attachment.name}</span>
          {typeof pages === 'number' ? (
            <span className="ml-auto shrink-0 opacity-65">
              {pages} {pages === 1 ? 'Seite' : 'Seiten'}
            </span>
          ) : null}
        </div>
      </button>
    );
  }

  // --- Generic file chip (also the fallback when inline media failed) ---
  const tone =
    variant === 'user'
      ? 'bg-white/15 hover:bg-white/25 text-user-bubble-text'
      : 'bg-elevated hover:bg-divider/60 text-text-primary border border-divider';

  return (
    <button
      type="button"
      onClick={downloadFull}
      disabled={!downloadable || busy}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-60 ${tone}`}
      title={attachment.name}
    >
      {busy ? <Spinner size={13} /> : <FileIcon size={13} />}
      <span className="truncate max-w-[14rem]">{attachment.name}</span>
      <span className="opacity-65">{humanSize(attachment.size)}</span>
      {error ? <span className="text-error-text font-bold">!</span> : null}
    </button>
  );
}
